import React, { useState, useEffect, forwardRef, useRef, useMemo } from 'react';
import { Chip, Collapse, Icon, TextField, IconButton } from '@mui/material';
import { AutoComplete } from '../Autocomplete';
import { get, size, uniqBy, noop } from '../../lib/lodash';
import { useStyles } from '../form/styles';
import { ChipContainer, InlineModal, StyledFormControl } from './styles';
import { SimpleSort } from '../sortable-list/simple';
import { useScreen } from '../../styles';
import { customSearch } from '../../utils';
import { Row } from '../row';
import Modal from '../modal';

/** Order-independent id key; used for value sync and highlight/compare equality. */
function selectionIdKey(rooms) {
  if (!size(rooms)) {
    return '';
  }
  return [...new Set(rooms.map((r) => String(r.id)))].sort().join(',');
}

export const getTradeName = (trade) => {
  if (!trade) { return '' }
  return trade.name || trade.defaultName;
}

export const RoomSelectComp = forwardRef((props, ref) => {
  const classes = useStyles({ showPosition: false });

  const [selected, setSelected] = useState(props.value || []);
  const [data, setData] = useState(props.rooms);
  const setByDefault = useRef(false);

  const valueSyncKey = useMemo(() => selectionIdKey(props.value), [props.value]);

  useEffect(() => {
    setSelected(props.value || []);
  }, [valueSyncKey]);

  useEffect(() => {
    setData(props.rooms || []);
  }, [props.rooms]);

  const sortable = get(props, 'sortable', false);

  const deleteSelected = (s, i) => {
    const sel = [...selected];
    sel.splice(i, 1);
    setSelected(sel);
    setByDefault.current = false;
    props.onChange && props.onChange(sel);
  }

  const onSort = result => {
    const next = result.map(r => ({ ...r, content: undefined }));
    setSelected(next);
    props.onChange && props.onChange(next);
  }

  const renderChip = (s, i) => {
    return <Chip size="small" icon={sortable ? <Icon>drag_indicator</Icon> : undefined} label={getTradeName(s)} onDelete={() => deleteSelected(s, i)} />;
  }

  return (
    <StyledFormControl variant="standard">
      <Collapse in={!!size(selected)}>
        <ChipContainer>
          {sortable && <SimpleSort onSort={onSort} items={selected.map((s, i) => ({ ...s, content: renderChip(s, i) }))} />}
          {!sortable && selected.map(renderChip)}
        </ChipContainer>
      </Collapse>

      <div className={!props.disabled && classes.formWrap}>
        <AutoComplete
          filterOptions={(o, s) => customSearch(o, s, { key: getTradeName })}
          classes={
            props.disabled
              ? {
                  groupLabel: classes.primaryColor,
                  tag: classes.userSelectChip,
                  endAdornment: classes.collapsedScreen,
                }
              : {
                  groupLabel: classes.primaryColor,
                }
          }
          topOffset={-180}
          variant="standard"
          multiple={get(props, 'multiple', true)}
          options={data}
          groupBy={(opt) => opt.groupBy}
          getOptionLabel={getTradeName}
          disableCloseOnSelect
          disableClearable
          openOnFocus
          renderInput={(params) => <TextField variant="standard" {...props} onChange={noop} label={undefined} {...params} />}
          renderTags={() => null}
          isOptionEqualToValue={(option, val) => option != null && val != null && option.id === val.id}
          {...props}
          value={selected || []}
          label={undefined}
          onChange={(e, vals) => {
            const upd = uniqBy(vals, 'id');
            setSelected(upd);
            setByDefault.current = false;
            props.onChange && props.onChange(upd);
          }}
        />
      </div>
    </StyledFormControl>
  );
})

export const RoomSelect = props => {
  const [selected, setSelected] = useState(props.value || []);
  const [original, setOriginal] = useState(props.value || []);
  const [state, setState] = useState({
    open: false,
  })

  const { isMobileXS } = useScreen();

  const valueSyncKey = useMemo(() => selectionIdKey(props.value), [props.value]);

  useEffect(() => {
    setSelected(props.value || []);
    setOriginal(props.value || []);
  }, [valueSyncKey]);

  const highlightOriginals = get(props, 'highlightOriginals', false);

  const compareRooms = get(props, 'compare', original) || [];
  const areEqual = highlightOriginals ? selectionIdKey(selected) === selectionIdKey(compareRooms) : false;

  const toggleModal = () => {
    setState(s => ({ ...s, open: !s.open }));
  }

  const copy = () => {
    props.onCopy && props.onCopy(selected);
  }

  const paste = () => {
    onChange(props.onPaste());
  }

  const onChange = sel => {
    setSelected(sel);
    props.onChange && props.onChange(sel, areEqual);
  }

  const deleteSelected = (s, i) => {
    const sel = [...selected];
    sel.splice(i, 1);
    setSelected(sel);
    props.onChange && props.onChange(sel);
  }

  const renderChip = (s, i) => {
    return <Chip size="small" style={areEqual && highlightOriginals ? { opacity: 0.6 } : { opacity: 1.0 }} label={getTradeName(s)} onDelete={() => deleteSelected(s, i)} />;
  }

  const type = get(props, 'type', 'default');

  if (type === 'inlineModal') {
    return (
      <InlineModal style={props.style}>
        <Row>
          <ChipContainer>
            {selected.map(renderChip)}
          </ChipContainer>
          <IconButton variant="filled" color="secondary" onClick={toggleModal} ><Icon>add_box</Icon></IconButton>
          <IconButton color="secondary" onClick={copy} disabled={!size(selected)} ><Icon>file_copy_outlined</Icon></IconButton>
          <IconButton color="secondary" disabled={!props.onPaste} onClick={paste} ><Icon>post_add_oulined</Icon></IconButton>
        </Row>

        <Modal fullHeight={isMobileXS} fullWidth={true} maxWidth={'sm'} open={state.open} onClose={toggleModal} title={props.modalTitle || "Add Trade Types"}>
          <div style={{ minHeight: 600 }}>
            {state.open && <RoomSelectComp autoFocus {...props} value={selected} onChange={onChange} />}
          </div>
        </Modal>
      </InlineModal>
    )
  }

  return <RoomSelectComp {...props} />
}